/**
 * Plan de tratamiento dentro de la atención: indicarlo y ver sus sesiones.
 *
 * LOS DOS CAMINOS
 *
 * El profesional indica cuántas sesiones hacen falta y elige: darles hora él
 * mismo ahora, o dejarlas indicadas para que admisión las agende. Lo segundo
 * es lo normal cuando hay cola —son dos clics y sigue con el siguiente— y es
 * lo que convierte una indicación de papel en un pendiente visible: el plan
 * sin agendar cae en la bandeja de quien agenda.
 *
 * Cada sesión se cobra por separado y ninguna pasa por triaje: el paciente ya
 * fue triado en esta consulta.
 */
import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { CalendarClock, CalendarPlus, Repeat, Send, Trash2 } from "lucide-react";
import { Link } from "react-router-dom";
import type { Cita } from "../../api/citas";
import {
  type Periodicidad,
  type Plan,
  PERIODICIDADES,
  anularPlan,
  crearPlan,
  listPlanes,
  quitarSesion,
} from "../../api/planes";
import { formatApiError } from "../../api/client";
import { useAuth } from "../../auth/AuthProvider";
import { useBase, useZonaHoraria } from "../../auth/TenantBrandingProvider";
import { fechaHora } from "../../lib/fecha";
import { AgendarSesionesDialog } from "./AgendarSesiones";
import { AnularDialog } from "./AnularDialog";
import { EstadoFlujoDeCita } from "./EstadoFlujo";
import {
  Alert,
  Badge,
  Button,
  Card,
  CardBody,
  CardHeader,
  ConfirmDialog,
  Dialog,
  Field,
  Input,
} from "../ui";

export function PlanTratamientoEpisodio({
  episodioId,
  cerrada = false,
  permitirAgregar = false,
}: {
  episodioId: number;
  /** La atención está firmada: no nace nada nuevo salvo que se pida aparte. */
  cerrada?: boolean;
  /** El médico pulsó "Añadir a esta atención" y sabe lo que hace. */
  permitirAgregar?: boolean;
}) {
  const { me } = useAuth();
  const queryClient = useQueryClient();
  const [nuevoOpen, setNuevoOpen] = useState(false);
  const [agendando, setAgendando] = useState<Plan | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [anulando, setAnulando] = useState<Plan | null>(null);
  const bloqueado = cerrada && !permitirAgregar;
  const [errorAnular, setErrorAnular] = useState<string | null>(null);

  const anular = useMutation({
    mutationFn: ({ id, motivo }: { id: number; motivo: string }) =>
      anularPlan(id, motivo),
    onSuccess: () => {
      setAnulando(null);
      setErrorAnular(null);
      queryClient.invalidateQueries({ queryKey: ["planes"] });
      queryClient.invalidateQueries({ queryKey: ["citas"] });
    },
    onError: (e) => setErrorAnular(formatApiError(e, "No se pudo anular el plan.")),
  });

  const puedeIndicar = me?.permisos.includes("hce:write") ?? false;
  const puedeVer = me?.permisos.includes("cita:read") ?? false;

  const { data: planes = [] } = useQuery({
    queryKey: ["planes", "episodio", episodioId],
    queryFn: () => listPlanes({ episodio_id: episodioId }),
    enabled: puedeVer,
  });

  if (!puedeVer && !puedeIndicar) return null;

  return (
    <Card>
      <CardHeader>
        <div className="flex flex-wrap items-center justify-between gap-2">
          <div>
            <h2 className="flex items-center gap-2 font-semibold text-slate-900">
              <Repeat className="h-4 w-4 text-[color:var(--color-link)]" aria-hidden="true" />
              Plan de tratamiento
            </h2>
            <p className="mt-0.5 text-xs text-slate-500">
              Varias sesiones con la misma indicación. Cada una se cobra; ninguna
              vuelve a pasar por triaje.
            </p>
          </div>
          {puedeIndicar && !bloqueado && planes.length === 0 && (
            <Button onClick={() => setNuevoOpen(true)}>
              <CalendarClock className="h-4 w-4" />
              Indicar sesiones
            </Button>
          )}
        </div>
      </CardHeader>
      <CardBody className="space-y-3">
        {error && <Alert>{error}</Alert>}
        {planes.length === 0 && (
          <p className="text-sm text-slate-400">
            Sin tratamiento programado. No toda consulta lo necesita.
          </p>
        )}
        {planes.map((p) => (
          <div key={p.id} className="space-y-1">
            <PlanCard plan={p} />
            {puedeIndicar && !bloqueado && p.estado === "ACTIVO" && (
              <div className="flex justify-end">
                <button
                  type="button"
                  onClick={() => {
                    setErrorAnular(null);
                    setAnulando(p);
                  }}
                  className="inline-flex items-center gap-1.5 rounded-lg px-2.5 py-1 text-xs font-medium text-rose-600 hover:bg-rose-50"
                >
                  <Trash2 className="h-3.5 w-3.5" />
                  Anular el plan
                </button>
              </div>
            )}
          </div>
        ))}
      </CardBody>

      <AnularDialog
        open={anulando != null}
        onClose={() => setAnulando(null)}
        titulo="Anular el plan de tratamiento"
        queSeAnula={
          anulando ? `Plan #${anulando.id} · ${anulando.sesiones_totales} sesiones` : ""
        }
        loading={anular.isPending}
        error={errorAnular}
        onConfirm={(motivo) => anulando && anular.mutate({ id: anulando.id, motivo })}
      />

      {nuevoOpen && (
        <IndicarPlanDialog
          episodioId={episodioId}
          onClose={() => setNuevoOpen(false)}
          onSaved={(paraAgendar) => {
            setNuevoOpen(false);
            setError(null);
            queryClient.invalidateQueries({ queryKey: ["planes"] });
            queryClient.invalidateQueries({ queryKey: ["mi-espacio"] });
            // Encadena: eligió ponerles hora él, así que el calendario se abre
            // solo. Obligarlo a buscar otro botón sería perder el impulso.
            if (paraAgendar) setAgendando(paraAgendar);
          }}
          onError={setError}
        />
      )}

      {agendando && (
        <AgendarSesionesDialog
          plan={agendando}
          onClose={() => setAgendando(null)}
        />
      )}
    </Card>
  );
}

/** El plan con su progreso y sus sesiones. Se reutiliza en la ficha del paciente. */
export function PlanCard({ plan }: { plan: Plan }) {
  const { me } = useAuth();
  const base = useBase();
  const tz = useZonaHoraria();
  const queryClient = useQueryClient();
  const [agendarOpen, setAgendarOpen] = useState(false);
  const [errorQuitar, setErrorQuitar] = useState<string | null>(null);
  // Quitar una sesión cancela una cita real: se confirma antes.
  const [porQuitar, setPorQuitar] = useState<Cita | null>(null);
  // Un tratamiento se maneja desde dentro: lo toca quien lo indicó o quien
  // agenda para toda la clínica (admisión, admin). Para cualquier otro
  // profesional es historia del paciente y se lee, no se administra — que un
  // médico pudiera mover o borrar las sesiones de un colega era un error.
  const esMio = me?.profesional_id === plan.profesional_id;
  const agendaParaTodos =
    me?.is_super_admin || (me?.permisos.includes("profesional:read_all") ?? false);
  const puedeAgendar =
    (esMio || agendaParaTodos) &&
    (me?.is_super_admin || (me?.permisos.includes("paciente:update") ?? false));
  const faltan = plan.sesiones_totales - plan.sesiones_agendadas;

  const quitar = useMutation({
    mutationFn: (citaId: number) => quitarSesion(plan.id, citaId),
    onSuccess: () => {
      setErrorQuitar(null);
      setPorQuitar(null);
      queryClient.invalidateQueries({ queryKey: ["planes"] });
      queryClient.invalidateQueries({ queryKey: ["citas"] });
      queryClient.invalidateQueries({ queryKey: ["mi-espacio"] });
    },
    onError: (e) =>
      setErrorQuitar(formatApiError(e, "No se pudo quitar la sesión.")),
  });

  return (
    <div className="rounded-lg border border-slate-100 p-3">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <p className="flex flex-wrap items-center gap-2 text-sm">
          <span className="font-medium text-slate-900">
            {plan.especialidad_nombre} · {plan.sesiones_totales}{" "}
            {plan.sesiones_totales === 1 ? "sesión" : "sesiones"}
          </span>
          <Badge
            variant={
              plan.estado === "COMPLETADO"
                ? "success"
                : plan.estado === "ANULADO"
                  ? "danger"
                  : faltan > 0
                    ? "warning"
                    : "neutral"
            }
          >
            {plan.estado === "COMPLETADO"
              ? "Completado"
              : plan.estado === "ANULADO"
                ? "Anulado"
                : faltan > 0
                  ? `${faltan} sin agendar`
                  : "Todas agendadas"}
          </Badge>
          <span className="text-xs text-slate-500">
            {PERIODICIDADES.find((x) => x.value === plan.periodicidad)?.label}
          </span>
        </p>
        {puedeAgendar && plan.estado === "ACTIVO" && faltan > 0 && (
          <Button variant="ghost" onClick={() => setAgendarOpen(true)}>
            <CalendarPlus className="h-4 w-4" />
            Ver agenda y dar hora
          </Button>
        )}
      </div>

      {/* Antes había aquí una tira de cajas numeradas para "ver el progreso de
          un vistazo". No se entendía: tres rectángulos con 1, 2 y 3 y el
          significado escondido en un tooltip. La lista de abajo ya dice número,
          fecha y estado de cada sesión, así que la tira solo repetía peor lo
          que ya estaba dicho. */}
      {plan.sesiones.length > 0 && (
        <ul className="mt-2 space-y-1">
          {plan.sesiones.map((c) => (
            <li key={c.id} className="flex flex-wrap items-center gap-2 text-sm">
              <span className="w-6 font-mono text-xs text-slate-400">
                {c.numero_sesion ?? "—"}
              </span>
              <Link
                to={`${base}/citas/${c.id}`}
                className="text-slate-800 hover:text-[color:var(--color-link)] hover:underline"
              >
                {fechaHora(c.inicio, tz)}
              </Link>
              <EstadoFlujoDeCita cita={c} />
              {/* Un dedazo en la hora tiene que poder deshacerse. Solo mientras
                  nadie haya cobrado ni atendido: a partir de ahí hay dinero o
                  historia clínica de por medio y lo resuelve otro. */}
              {puedeAgendar && plan.estado === "ACTIVO" && (
                <button
                  type="button"
                  onClick={() => setPorQuitar(c)}
                  disabled={quitar.isPending}
                  className="ml-auto inline-flex items-center gap-1 rounded px-2 py-0.5 text-xs font-medium text-slate-500 hover:bg-red-50 hover:text-red-700 disabled:opacity-40"
                >
                  <Trash2 className="h-3.5 w-3.5" />
                  Quitar
                </button>
              )}
            </li>
          ))}
        </ul>
      )}

      {errorQuitar && (
        <p className="mt-2 text-xs text-red-600">{errorQuitar}</p>
      )}

      {/* Cuántas faltan, dicho con palabras. */}
      {plan.estado === "ACTIVO" && faltan > 0 && (
        <p className="mt-2 text-xs text-slate-500">
          {faltan === 1 ? "Falta 1 sesión" : `Faltan ${faltan} sesiones`} por
          agendar. Mientras tanto está en la bandeja de admisión.
        </p>
      )}

      {plan.indicaciones && (
        <p className="mt-2 text-xs italic text-slate-500">{plan.indicaciones}</p>
      )}

      {agendarOpen && (
        <AgendarSesionesDialog
          plan={plan}
          onClose={() => setAgendarOpen(false)}
        />
      )}

      <ConfirmDialog
        open={porQuitar !== null}
        onClose={() => setPorQuitar(null)}
        onConfirm={() => porQuitar && quitar.mutate(porQuitar.id)}
        loading={quitar.isPending}
        title="Quitar sesión"
        description="Se cancela esa cita y la sesión vuelve a quedar sin hora, lista para reprogramarse."
        detalle={
          porQuitar && (
            <>
              Sesión {porQuitar.numero_sesion ?? "—"} ·{" "}
              <span className="font-medium">{fechaHora(porQuitar.inicio, tz)}</span>
            </>
          )
        }
        confirmLabel="Sí, quitarla"
        cancelLabel="No, dejarla"
      />
    </div>
  );
}

function IndicarPlanDialog({
  episodioId,
  onClose,
  onSaved,
  onError,
}: {
  episodioId: number;
  onClose: () => void;
  /** Devuelve el plan si el profesional quiere agendarlo él mismo. */
  onSaved: (paraAgendar: Plan | null) => void;
  onError: (m: string) => void;
}) {
  const [sesiones, setSesiones] = useState("3");
  const [periodicidad, setPeriodicidad] = useState<Periodicidad>("SEMANAL");
  const [indicaciones, setIndicaciones] = useState("");

  const guardar = useMutation({
    // El booleano dice si, al crearlo, se abre el agendador o se deja para
    // admisión. La diferencia entre las dos vías es solo esto.
    mutationFn: (_agendarYo: boolean) =>
      crearPlan({
        episodio_id: episodioId,
        sesiones_totales: Math.max(1, Number(sesiones) || 1),
        periodicidad,
        indicaciones: indicaciones.trim() || null,
      }),
    onSuccess: (plan, agendarYo) => onSaved(agendarYo ? plan : null),
    onError: (e) => onError(formatApiError(e, "No se pudo indicar el tratamiento.")),
  });

  return (
    <Dialog
      open
      onClose={onClose}
      title="Indicar tratamiento"
      description="Cuántas sesiones y cada cuánto. Las horas se ponen ahora o las pone admisión."
    >
      <div className="space-y-4">
        <div className="grid grid-cols-2 gap-3">
          <Field label="Sesiones" htmlFor="pl_n">
            <Input
              id="pl_n"
              type="number"
              min={1}
              max={60}
              value={sesiones}
              onChange={(e) => setSesiones(e.target.value)}
              className="font-mono"
            />
          </Field>
          <Field label="Cada cuánto" htmlFor="pl_per">
            <select
              id="pl_per"
              value={periodicidad}
              onChange={(e) => setPeriodicidad(e.target.value as Periodicidad)}
              className="w-full rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm"
            >
              {PERIODICIDADES.map((p) => (
                <option key={p.value} value={p.value}>
                  {p.label}
                </option>
              ))}
            </select>
          </Field>
        </div>

        <Field
          label="Indicaciones"
          htmlFor="pl_ind"
          hint="Qué se trabaja en las sesiones. Lo lee quien atienda cada una."
        >
          <textarea
            id="pl_ind"
            value={indicaciones}
            onChange={(e) => setIndicaciones(e.target.value)}
            rows={2}
            className="w-full rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm"
          />
        </Field>

        {/* Las dos vías, dichas como decisión y no como nota al pie. Antes esto
            era un párrafo informativo y había que adivinar que agendar uno
            mismo era posible: el médico salía del diálogo sin saber qué pasaba
            con las horas. */}
        <div className="rounded-lg border border-slate-200 p-3">
          <p className="text-xs font-semibold uppercase tracking-wide text-slate-400">
            ¿Quién les pone hora?
          </p>
          <p className="mt-1 text-sm text-slate-600">
            Lo que dejes sin hora cae en la bandeja de admisión como pendiente de
            agendar. No se pierde.
          </p>
        </div>

        {/* Los dos del mismo ancho y en su propia fila.

            "Indicar y enviar a admisión" / "Indicar y agendar yo" repetía en
            cada botón el verbo del título del diálogo —indicar— que además no
            dice nada: lo que el médico elige aquí es QUIÉN pone las horas.
            Y con anchos distintos, el más largo se leía como el importante
            cuando las dos salidas valen igual. */}
        <div className="flex flex-wrap items-center justify-end gap-2">
          <Button variant="ghost" onClick={onClose}>
            Cancelar
          </Button>
          <div className="grid flex-1 grid-cols-2 gap-2 sm:flex-none sm:w-96">
            <Button
              variant="secondary"
              onClick={() => guardar.mutate(false)}
              disabled={guardar.isPending}
              className="w-full justify-center"
            >
              <Send className="h-4 w-4" />
              Enviar a admisión
            </Button>
            <Button
              onClick={() => guardar.mutate(true)}
              disabled={guardar.isPending}
              className="w-full justify-center"
            >
              <CalendarPlus className="h-4 w-4" />
              Agendar yo
            </Button>
          </div>
        </div>
      </div>
    </Dialog>
  );
}
